import React, { useEffect, useRef, useState } from "react";
import "./add_line_for_goods_receive.css";
import { SearchOutlined } from "@ant-design/icons";
import type { InputRef, TableColumnsType, TableColumnType } from "antd";
import { Button, Input, Space, Table } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import useSWR from "swr";
import axios from "axios";
import { PoItemDataType } from "../../components/hoc_goods_receive";

export interface AddLineForGRDataType {
     key: string;
     itemCode: string;
     itemName: string;
     qty: number;
     itemPrice: number;
}

interface AddLineForGRProps {
     poNo: string | undefined;
     onAddItems: (items: AddLineForGRDataType[]) => void;
     existingItems: PoItemDataType[];
     initialSelectedKeys?: React.Key[];
}

const fetcher = (url: string) => axios.get(url).then((res) => res.data);
const AddLineForGR: React.FC<AddLineForGRProps> = ({ poNo, onAddItems, existingItems, initialSelectedKeys }) => {
     const transformStyle = { transform: "scale(0.80)", transformOrigin: "top left", width: "125%" };
     type DataIndex = keyof AddLineForGRDataType;
     const [searchText, setSearchText] = useState("");
     const [searchedColumn, setSearchedColumn] = useState("");
     const searchInput = useRef<InputRef>(null);
     const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
     useEffect(() => {
          setSelectedRowKeys(initialSelectedKeys || []);
     }, [initialSelectedKeys]);
     const { data: fetchedPoItems = [], error: poItemsError } = useSWR<any[]>(
          poNo ? `${process.env.REACT_APP_BACKEND_URL}/api/goods-receive-incomplete-po-items/${poNo}` : null, 
          fetcher,
     );

     if (poItemsError) {
          console.error("Error fetching PO items for Add Line:", poItemsError);
     }

     const data: AddLineForGRDataType[] = fetchedPoItems.map((item) => ({
          key: item.item_code,
          itemCode: item.item_code,
          itemName: item.item_name,
          qty: parseFloat(item.original_po_qty) - parseFloat(item.total_received_qty), 
          itemPrice: item.price - (item.discount || 0),
     }));

     const allItemsAlreadyAdded = data.length > 0 && data.every(item =>
          existingItems.some(existingItem => existingItem.item_code === item.itemCode)
     );
     const isOkButtonDisabled = allItemsAlreadyAdded || selectedRowKeys.length === 0;

     const handleSearch = (
          selectedKeys: string[],
          confirm: FilterDropdownProps["confirm"],
          dataIndex: DataIndex,
     ) => {
          confirm();
          setSearchText(selectedKeys[0]);
          setSearchedColumn(dataIndex);
     };

     const handleReset = (clearFilters: () => void) => {
          clearFilters();
          setSearchText("");
     };

     const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<AddLineForGRDataType> => ({
          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
               <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                         ref={searchInput}
                         placeholder={`Search ${String(dataIndex)
                              .replace(/([A-Z])/g, " $1")
                              .trim()}`}
                         value={selectedKeys[0]}
                         onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                         onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                         style={{ marginBottom: 8, display: "block" }}
                    />
                    <Space>
                         <Button
                              type="primary"
                              onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                              icon={<SearchOutlined />}
                              size="small"
                              style={{ width: 90 }}>
                              Search
                         </Button>
                         <Button
                              onClick={() => clearFilters && handleReset(clearFilters)}
                              size="small"
                              style={{ width: 90 }}>
                              Reset
                         </Button>
                         <Button
                              type="link"
                              size="small"
                              onClick={() => {
                                   confirm({ closeDropdown: false });
                                   setSearchText((selectedKeys as string[])[0]);
                                   setSearchedColumn(dataIndex);
                              }}>
                              Filter
                         </Button>
                         <Button
                              type="link"
                              size="small"
                              onClick={() => {
                                   close();
                              }}>
                              close
                         </Button>
                    </Space>
               </div>
          ),
          filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />,
          onFilter: (value, record) =>
               record[dataIndex]
                    .toString()
                    .toLowerCase()
                    .includes((value as string).toLowerCase()),
          filterDropdownProps: {
               onOpenChange(open) {
                    if (open) {
                         setTimeout(() => searchInput.current?.select(), 100);
                    }
               },
          },
          render: (text) =>
               searchedColumn === dataIndex ? (
                    <Highlighter
                         highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                         searchWords={[searchText]}
                         autoEscape
                         textToHighlight={text ? text.toString() : ""}
                    />
               ) : (
                    text
               ),
     });

     const rowSelection = {
          selectedRowKeys,
          onChange: (newSelectedRowKeys: React.Key[]) => {
               setSelectedRowKeys(newSelectedRowKeys);
          },
          getCheckboxProps: (record: AddLineForGRDataType) => ({
               disabled: existingItems.some((existingItem) => existingItem.item_code === record.itemCode),
               name: record.itemCode,
          }),
     };

     const columns: TableColumnsType<AddLineForGRDataType> = [
          {
               title: "Item Code",
               dataIndex: "itemCode",
               key: "itemCode",
               width: "25%",
               ...getColumnSearchProps("itemCode"),
               sorter: (a, b) => a.itemCode.localeCompare(b.itemCode),
               sortDirections: ["ascend", "descend"],
          },
          {
               title: "Item Name",
               dataIndex: "itemName",
               key: "itemName",
               width: "35%",
               ...getColumnSearchProps("itemName"),
               sorter: (a, b) => a.itemName.localeCompare(b.itemName),
               sortDirections: ["ascend", "descend"],
          },
          {
               title: "Qty",
               dataIndex: "qty",
               key: "qty",
               width: "20%",
               ...getColumnSearchProps("qty"),
               sorter: (a, b) => a.qty - b.qty,
               sortDirections: ["ascend", "descend"],
          },
          {
               title: "Item Price",
               dataIndex: "itemPrice",
               key: "itemPrice",
               width: "20%",
               ...getColumnSearchProps("itemPrice"),
               sorter: (a, b) => a.itemPrice - b.itemPrice,
               sortDirections: ["ascend", "descend"],
               render: (text: number) => text.toLocaleString("id-ID"),
          },
     ];

     return (
          <div id="ieml" style={transformStyle}>
               <div className="gjs-grid-row" id="iu3g">
                    <div className="gjs-grid-column" id="iluh">
                         <Table<AddLineForGRDataType>
                              columns={columns}
                              dataSource={data}
                              rowSelection={rowSelection}
                              rowKey="key"
                         />
                         <Space style={{ paddingTop: "20px" }}>
                              <Button
                                   type="primary"
                                   htmlType="submit"
                                   style={{
                                        height: "40px",
                                        width: "120px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                   }}
                                   icon={
                                        <svg
                                             xmlns="http://www.w3.org/2000/svg"
                                             viewBox="0 0 24 24"
                                             fill="currentColor"
                                             style={{ width: "22px", height: "22px" }}>
                                             <path
                                                  fillRule="evenodd"
                                                  d="M19.916 4.626a.75.75 0 0 1 .208 1.04l-9 13.5a.75.75 0 0 1-1.154.114l-6-6a.75.75 0 0 1 1.06-1.06l5.353 5.353 8.493-12.74a.75.75 0 0 1 1.04-.207Z"
                                                  clipRule="evenodd"
                                             />
                                        </svg>
                                   }
                                   disabled={isOkButtonDisabled}
                                   onClick={() => {
                                        const selectedItems = data.filter((item) =>
                                             selectedRowKeys.includes(item.key)
                                        );
                                        const newItemsToAdd = selectedItems.filter(
                                             (newItem) => !existingItems.some(
                                                  (existing) => existing.item_code === newItem.itemCode
                                             )
                                        );
                                        onAddItems(newItemsToAdd);

                                   }}>
                                   OK
                              </Button>
                              <Button
                                   type="primary"
                                   style={{
                                        height: "40px",
                                        width: "120px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: "red",
                                        color: "white",
                                        border: "none",
                                   }}>
                                   Cancel
                              </Button>
                         </Space>
                    </div>
               </div>
          </div>
     );
};

export default AddLineForGR;